import axios from 'axios';
import { serverEndPoint } from './EnvironmentVariables';
import Logger from '../../Logger';
import { jwtDecode } from 'jwt-decode';
import { resetUser, updateToken } from '../../redux/UserInfo/isLoggedActions';
import Cookies from 'js-cookie';
import { getReduxDispatch } from '../../redux/dispatchHelper';

type FetchDataPropType = {
  requestType: 'withData' | 'noData';
  method: string;
  url: string;
  checkToken?: boolean;
  data?: any;
  headers: any;
  token?: string;
  body?: any;
  baseUrl?: any;
};

export interface IAPIResponse<T> {
  status: boolean;
  data: T | null;
  error: string | null;
  responseStatusCode?: number;
}

// Function to refresh the access token
const refreshToken = async (): Promise<string | undefined> => {
  try {
    const refreshTokenCookie = Cookies.get('refreshToken');
    if (!refreshTokenCookie) return undefined;
    const response = await axios.get(`${serverEndPoint}refresh-token`, {
      headers: {
        Authorization: `Bearer ${refreshTokenCookie}`,
      },
      timeout: 60 * 1000,
    });

    const newToken = response.data.accessToken;
    return newToken;
  } catch (error) {
    console.error('Token refresh failed', error);
    return undefined;
  }
};

// Function to check if the token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = jwtDecode(token);
    if (!payload.exp) return true;
    const expiryTime = payload.exp * 1000;
    return Date.now() >= expiryTime;
  } catch (error) {
    console.error('Invalid token', error);
    return true;
  }
};

// Function to make API requests
const FetchData = async <T = any,>(props: FetchDataPropType): Promise<IAPIResponse<T>> => {
  let { requestType, method, url, headers, data, checkToken = true } = props;
  let token = Cookies.get('authToken');
  const dispatch = getReduxDispatch();
  if (checkToken) {
    // Check token validity
    if (!token || isTokenExpired(token)) {
      token = await refreshToken();
      if (!token) {
        if (dispatch) dispatch(resetUser());
        return {
          status: false,
          data: null,
          error: 'Token Expired. Please login again.',
          responseStatusCode: 401,
        };
      } else if (dispatch) dispatch(updateToken(token));
    }

    headers = { ...(headers || {}), Authorization: `Bearer ${token}` };
  }

  const makeRequest = async (retryCount = 0): Promise<IAPIResponse<T>> => {
    if (retryCount > 1) {
      return {
        status: false,
        data: null,
        error: 'Too many retries',
        responseStatusCode: 401,
      };
    }
    try {
      const fullUrl =
        url.startsWith('http://') || url.startsWith('https://') ? url : `${serverEndPoint}${url}`;

      const response = await axios<T>({
        method,
        url: fullUrl,
        headers,
        data: requestType === 'withData' ? data : undefined,
        timeout: 60 * 1000,
      });

      return {
        status: true,
        data: response.data,
        error: null,
        responseStatusCode: response.status,
      };
    } catch (err: any) {
      Logger('error', err);
      if (err.response?.status === 401) {
        token = await refreshToken();
        if (token) {
          headers = { ...(headers || {}), Authorization: `Bearer ${token}` };
          if (dispatch) dispatch(updateToken(token));
          return makeRequest(retryCount + 1);
        }
        if (dispatch) dispatch(resetUser());
        return {
          status: false,
          data: null,
          error: 'Token Expired. Please login again.',
          responseStatusCode: 401,
        };
      }
      return {
        status: false,
        data: null,
        error:
          typeof err?.response?.data === 'string'
            ? err.response.data
            : err?.response?.data?.message ||
              err?.response?.data?.detail ||
              err?.message ||
              'Unknown error',
        responseStatusCode: err.response?.status || 500,
      };
    }
  };

  return makeRequest();
};

export default FetchData;
